import React, { useState, useEffect, useCallback } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { getAllCompanyUsersApi } from "actions/userAction";
import { formatDate, getUserRole } from "utils/helper";
import EditUserModal from "./EditUserModal";

// Map a resolved role string to the prototype's 4 role buckets.
const normalizeRole = (raw) => {
  const r = (raw || "").toString().toLowerCase();
  if (["admin", "super_admin", "owner"].includes(r)) return "admin";
  if (r === "bom_admin") return "bom_admin";
  if (r === "order_admin") return "order_admin";
  return "user";
};

const ROLE_LABEL = {
  admin: "Admin",
  bom_admin: "BOM Admin",
  order_admin: "Order Admin",
  user: "User",
};

// Prototype role-pill colors (bg / text).
const ROLE_PILL = {
  admin: "bg-s360-teal-50 text-[#0a5450]",
  bom_admin: "bg-s360-violet-50 text-s360-violet-900",
  order_admin: "bg-s360-warning-bg text-s360-warning-ink",
  user: "bg-s360-bg2 text-s360-fg2",
};

const initialsOf = (name = "", email = "") => {
  const source = (name || "").trim() || (email || "").trim();
  if (!source) return "?";
  return (
    source
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "?"
  );
};

const CompanyUsersList = ({ refreshKey = 0 }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [tableRefreshTrigger, setTableRefreshTrigger] = useState(0);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAllCompanyUsersApi();
      if (response?.status) {
        const data = response.data;
        const list = Array.isArray(data)
          ? data
          : data?.results || data?.data || data?.users || data?.rows || [];
        setUsers(Array.isArray(list) ? list : []);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, tableRefreshTrigger, refreshKey]);

  const handleEditUser = (item) => {
    setEditUser(item);
    setIsEditUserModalOpen(true);
  };

  const handleDeleteUser = (item) => {
    console.log("handleDeleteUser", { item });
  };

  return (
    <>
      <div className="flex flex-col">
        {isLoading ? (
          <div className="px-6 py-9 text-center text-sm text-s360-fg3">
            Loading members…
          </div>
        ) : users.length === 0 ? (
          <div className="px-6 py-9 text-center text-sm text-s360-fg3">
            No team members yet.
          </div>
        ) : (
          users.map((u, idx) => {
            const role = normalizeRole(getUserRole(u));
            const name = u?.name || u?.full_name || u?.email || "—";
            const email = u?.email || "";
            const lastActive = u?.created_at ? formatDate(u.created_at) : "";
            const isAdmin = role === "admin";
            return (
              <div
                key={u?.id ?? u?.email ?? idx}
                className="flex items-center gap-3.5 px-6 py-3 border-b border-s360-bg2 last:border-b-0"
              >
                <span
                  className={`w-9 h-9 rounded-full inline-flex items-center justify-center text-xs font-bold shrink-0 ${
                    isAdmin
                      ? "bg-s360-teal text-s360-ink"
                      : "bg-s360-violet text-white"
                  }`}
                >
                  {initialsOf(name, email)}
                </span>

                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="text-sm font-semibold text-s360-fg1 truncate">
                    {name}
                  </span>
                  <span className="text-xs text-s360-fg3 truncate">{email}</span>
                </div>

                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] leading-[14px] font-semibold ${ROLE_PILL[role]}`}
                >
                  {ROLE_LABEL[role]}
                </span>

                {lastActive && (
                  <span className="font-mono text-[11px] text-s360-fg3 whitespace-nowrap">
                    {lastActive}
                  </span>
                )}

                <div className="inline-flex gap-1 shrink-0">
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => handleEditUser(u)}
                    className="w-7 h-7 inline-flex items-center justify-center rounded border border-s360-stroke1 text-s360-fg2 hover:bg-s360-violet-50 hover:text-s360-violet transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {!isAdmin && (
                    <button
                      type="button"
                      title="Remove"
                      onClick={() => handleDeleteUser(u)}
                      className="w-7 h-7 inline-flex items-center justify-center rounded border border-s360-stroke1 text-s360-warning hover:bg-s360-warning-bg transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <EditUserModal
        isEditUserModalOpen={isEditUserModalOpen}
        setIsEditUserModalOpen={setIsEditUserModalOpen}
        editUser={editUser}
        isLoading={isLoading}
        onUpdateSuccess={() => setTableRefreshTrigger((t) => t + 1)}
      />
    </>
  );
};

export default CompanyUsersList;
